/**
 * NH 투자 — 매매(보유→주문→주문확인 통합 흐름) + 배분 룰 자동매매
 * Tabs: 매매(보유 종목 행에서 매수/매도 → 미체결 즉시 확인 → 주문 내역) / 자동매매
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App as AntApp,
  AutoComplete,
  Button,
  Card,
  Grid,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import { PlayCircleOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  nhApi,
  type NhJudgmentCategory,
  type NhOrderRow,
  type NhQuote,
  type NhSignal,
} from "src/api/nh";
import { marketApi, type MarketSearchResult } from "src/api/market";
import { useTabSearchParam } from "src/hooks/useTabSearchParam";
import { QueryError } from "src/components/QueryError";
import StockDetailDrawer from "src/components/StockDetailDrawer";
import { formatWon } from "src/lib/format";
import { extractErrMsg } from "src/lib/errors";

const MODE_META: Record<string, { color: string; label: string }> = {
  off: { color: "default", label: "정지" },
  dry_run: { color: "gold", label: "DRY-RUN" },
  live: { color: "red", label: "LIVE" },
};

const SIGNAL_META: Record<string, { color: string; label: string }> = {
  wait: { color: "default", label: "대기" },
  buy: { color: "red", label: "매수" },
  buy_x2: { color: "volcano", label: "매수 2배" },
  sell: { color: "blue", label: "매도" },
};

const STATUS_META: Record<string, { color: string; label: string }> = {
  dry_run: { color: "gold", label: "dry-run" },
  signaled: { color: "orange", label: "대기중" },
  submitted: { color: "processing", label: "주문접수" },
  filled: { color: "success", label: "체결" },
  failed: { color: "error", label: "실패" },
  skipped: { color: "default", label: "건너뜀" },
};

const ORDER_TYPES = [
  { value: "00", label: "지정가" },
  { value: "03", label: "시장가" },
  { value: "61", label: "프리마켓" },
  { value: "62", label: "애프터마켓" },
] as const;

const UP = "#dc3912";
const DOWN = "#3366cc";

/** 주문 단가 래더 — 현재가 기준 ±0.5% 스텝 5개 (매수=아래, 매도=위) */
const LADDER_STEPS = [-0.5, -1.0, -1.5, -2.0, -2.5] as const;
const LADDER_STEPS_SELL = [0.5, 1.0, 1.5, 2.0, 2.5] as const;

const fmtUsd = (v?: number | string | null, digits = 2) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

const fmtPct = (v?: number | string | null, digits = 2) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}%p`;
};

/** 상승 적색·하락 청색 (국내 관례) */
const PctText = ({ v, suffix = "%" }: { v?: number | string | null; suffix?: string }) => {
  if (v === null || v === undefined || v === "") return <span>-</span>;
  const n = Number(v);
  if (Number.isNaN(n)) return <span>-</span>;
  return (
    <span style={{ color: n > 0 ? UP : n < 0 ? DOWN : undefined }}>
      {n > 0 ? "+" : ""}
      {n.toFixed(2)}
      {suffix}
    </span>
  );
};

/** 주문 단가 vs 전일종가 (%) */
const pctVsPrev = (price?: number | null, prevClose?: number | null): number | null => {
  if (!price || !prevClose || price <= 0) return null;
  return ((price / prevClose - 1) * 100);
};

// ═══════════════ 주문 모달 ═══════════════

export interface OrderPreset {
  ticker?: string;
  name?: string;
  side: "buy" | "sell";
}

const OrderModal = ({
  open,
  preset,
  onClose,
}: {
  open: boolean;
  preset: OrderPreset | null;
  onClose: () => void;
}) => {
  const { message } = AntApp.useApp();
  const qc = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState<MarketSearchResult[]>([]);
  const [ticker, setTicker] = useState<string | null>(preset?.ticker ?? null);
  const [tickerName, setTickerName] = useState(preset?.name ?? "");
  const [side, setSide] = useState<"buy" | "sell">(preset?.side ?? "buy");
  const [orderType, setOrderType] = useState<string>("00");
  const [qty, setQty] = useState<number | undefined>(1);
  const [price, setPrice] = useState<number | undefined>();

  const isMarket = orderType === "03";
  const tickerKey = open ? ticker : null;

  const quoteQ = useQuery({
    queryKey: ["nh", "quote", tickerKey],
    queryFn: () => nhApi.getQuote(tickerKey!),
    enabled: !!tickerKey,
    staleTime: 15_000,
  });
  const quote: NhQuote | null = quoteQ.data?.quote ?? null;
  const trdprc = quote ? Number(quote.trdprc ?? 0) : 0;
  const prevClose = useMemo(() => {
    if (!quote) return null;
    const trd = Number(quote.trdprc ?? 0);
    const netchng = Number(quote.netchng ?? 0);
    if (trd <= 0) return null;
    const prev = trd - netchng;
    return prev > 0 ? Math.round(prev * 100) / 100 : null;
  }, [quote]);

  const orderMut = useMutation({
    mutationFn: nhApi.placeOrder,
    onSuccess: (res) => {
      message.success(`주문 접수: ${res.rsp_msg ?? "완료"} — 미체결 목록 확인`);
      qc.invalidateQueries({ queryKey: ["nh"] });
      onClose();
    },
    onError: (e) => message.error(extractErrMsg(e)),
  });

  const buyableQ = useQuery({
    queryKey: ["nh", "buyable", tickerKey, side, isMarket ? null : price],
    queryFn: () => nhApi.getBuyable(tickerKey!, side === "buy" ? "2" : "3", isMarket ? undefined : price),
    enabled: !!tickerKey,
    staleTime: 15_000,
  });
  const buyable = buyableQ.data?.result ?? {};
  const buyableQty =
    side === "buy"
      ? Number(buyable.byn_pbl_qty ?? buyable.max_pbl_qty ?? 0)
      : Number(buyable.sll_pbl_qty ?? 0);
  const orderPblAmt = Number(buyable.orr_pbl_amt ?? 0);

  const applyAmount = (amount: number | undefined) => {
    if (amount && amount > 0 && price && price > 0) setQty(Math.floor(amount / price));
  };

  const runSearch = async (text: string) => {
    setSearchText(text);
    if (!text.trim()) {
      setOptions([]);
      return;
    }
    try {
      const results = await marketApi.search(text.trim(), true);
      setOptions(results);
    } catch {
      setOptions([]);
    }
  };

  const submit = () => {
    if (!ticker) {
      message.warning("종목을 선택하세요");
      return;
    }
    if (!qty || qty <= 0) {
      message.warning("수량을 입력하세요");
      return;
    }
    if (!isMarket && (!price || price <= 0)) {
      message.warning("주문 단가를 입력하세요");
      return;
    }
    if (buyableQty > 0 && qty > buyableQty) {
      message.warning(
        side === "buy"
          ? `매수가능 ${buyableQty}주를 초과했습니다 (주문가능 $${orderPblAmt.toFixed(0)})`
          : `매도가능 ${buyableQty}주를 초과했습니다`,
      );
      return;
    }
    const amt = isMarket ? "시장가" : `$${(price ?? 0).toFixed(2)}`;
    Modal.confirm({
      title: `${side === "buy" ? "매수" : "매도"} 주문`,
      content: `${tickerName || ticker}(${ticker}) ${qty}주 · ${ORDER_TYPES.find((t) => t.value === orderType)?.label} ${amt}`,
      okText: "주문",
      okButtonProps: { danger: side === "buy" },
      onOk: () =>
        orderMut.mutateAsync({
          side,
          iem_cd: ticker,
          qty,
          price: isMarket ? null : price!,
          order_type: orderType,
          dry_run: false,
        }),
    });
  };

  const pct = pctVsPrev(!isMarket ? price : null, prevClose);

  return (
    <Modal
      title="주문"
      open={open}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>닫기</Button>
          <Button type="primary" danger={side === "buy"} loading={orderMut.isPending} onClick={submit}>
            {side === "buy" ? "매수" : "매도"} 주문
          </Button>
        </Space>
      }
      width={560}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space wrap size="middle" align="center">
          <Space size={4}>
            <Typography.Text type="secondary">종목</Typography.Text>
            {preset?.ticker ? (
              <span>{preset.name || preset.ticker} ({preset.ticker})</span>
            ) : (
              <AutoComplete
                style={{ width: 220 }}
                value={searchText || (ticker ? `${tickerName || ticker} (${ticker})` : "")}
                onSearch={runSearch}
                onSelect={(_v, opt) => {
                  const t = opt as unknown as MarketSearchResult;
                  setTicker(t.code);
                  setTickerName(t.name);
                  setSearchText("");
                }}
                options={options.map((o) => ({ value: o.code, label: `${o.name} (${o.code})` }))}
                placeholder="티커·종목명 검색"
                allowClear
                onClear={() => {
                  setTicker(null);
                  setTickerName("");
                  setSearchText("");
                }}
              />
            )}
          </Space>
          <Space size={4}>
            <Typography.Text type="secondary">매매</Typography.Text>
            <Segmented
              value={side}
              onChange={(v) => setSide(v as "buy" | "sell")}
              options={[
                { label: "매수", value: "buy" },
                { label: "매도", value: "sell" },
              ]}
            />
          </Space>
        </Space>

        {quote && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            현재가 {fmtUsd(trdprc)} · 전일종가 {fmtUsd(prevClose)} (<PctText v={quote.pctchng} />)
          </Typography.Text>
        )}

        <Space wrap size="middle" align="center">
          <Space size={4}>
            <Typography.Text type="secondary">주문유형</Typography.Text>
            <Segmented
              value={orderType}
              onChange={(v) => setOrderType(v as string)}
              options={ORDER_TYPES.map((t) => ({ label: t.label, value: t.value }))}
            />
          </Space>
          <Space size={4}>
            <Typography.Text type="secondary">수량</Typography.Text>
            <InputNumber size="small" min={1} value={qty} onChange={(v) => setQty(v as number)} style={{ width: 70 }} />
          </Space>
          <Space size={4}>
            <Typography.Text type="secondary">금액 $</Typography.Text>
            <InputNumber
              size="small"
              min={0}
              step={100}
              disabled={isMarket || !price}
              placeholder="→ 수량"
              onChange={(v) => applyAmount(v as number)}
              style={{ width: 90 }}
            />
          </Space>
          <Space size={4}>
            <Typography.Text type="secondary">단가 $</Typography.Text>
            <InputNumber
              size="small"
              min={0}
              step={0.5}
              disabled={isMarket}
              value={isMarket ? undefined : price}
              onChange={(v) => setPrice(v as number)}
              style={{ width: 90 }}
            />
            {!isMarket && quote && (
              <Button size="small" type="link" onClick={() => setPrice(Math.round(trdprc * 100) / 100)}>
                현재가
              </Button>
            )}
          </Space>
        </Space>

        {ticker && buyableQty > 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {side === "buy"
              ? `주문가능 $${orderPblAmt.toFixed(0)} · 매수가능 ${buyableQty}주${!isMarket && price ? ` (@$${price.toFixed(2)})` : ""}`
              : `매도가능 ${buyableQty}주`}
            {buyableQ.isFetching ? " (조회중…)" : ""}
          </Typography.Text>
        )}

        {quote && !isMarket && prevClose && trdprc > 0 && (
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {side === "buy" ? "현재가 대비 -0.5% 스텝 (낮은 금액)" : "현재가 대비 +0.5% 스텝 (높은 금액)"} — 퍼센트는 전일종가 대비 · 클릭해 단가 선택
            </Typography.Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
              {(side === "buy" ? LADDER_STEPS : LADDER_STEPS_SELL).map((step) => {
                const amt = Math.round(trdprc * (1 + step / 100) * 100) / 100;
                const pctFromPrev = pctVsPrev(amt, prevClose);
                const selected = !!price && Math.abs(price - amt) < 0.005;
                return (
                  <Button
                    key={step}
                    size="small"
                    type={selected ? "primary" : "default"}
                    danger={selected && side === "buy"}
                    style={{ lineHeight: 1.2, padding: "2px 8px", height: "auto" }}
                    onClick={() => setPrice(amt)}
                  >
                    <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: (pctFromPrev ?? 0) >= 0 ? UP : DOWN }}>
                      {(pctFromPrev ?? 0) > 0 ? "+" : ""}{(pctFromPrev ?? 0).toFixed(2)}%
                    </span>
                    <span style={{ display: "block", fontSize: 11 }}>${amt.toFixed(2)}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {quote && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {isMarket ? (
              "시장가 — 전일종가 기준 변동은 체결 후 확인 (정규장 전용)"
            ) : (
              <>
                주문가 전일종가 대비 <PctText v={pct} />
                {prevClose && price ? ` ($${price.toFixed(2)} vs $${prevClose.toFixed(2)})` : ""}
                {" · 프리마켓/애프터마켓은 지정가만 가능"}
              </>
            )}
          </Typography.Text>
        )}
      </Space>
    </Modal>
  );
};

// ═══════════════ 정정 모달 ═══════════════

const ModifyModal = ({
  row,
  prevClose,
  trdprc,
  onClose,
}: {
  row: NhOrderRow;
  prevClose: number | null;
  trdprc: number;
  onClose: () => void;
}) => {
  const { message } = AntApp.useApp();
  const qc = useQueryClient();
  const side: "buy" | "sell" = row.oss_sby_dit_cd === "2" ? "buy" : "sell";
  const [price, setPrice] = useState<number | undefined>(row.fc_orr_uit_pr || undefined);

  const mut = useMutation({
    mutationFn: nhApi.modifyOrder,
    onSuccess: (res) => {
      message.success(`정정 접수: ${res.rsp_msg ?? "완료"}`);
      qc.invalidateQueries({ queryKey: ["nh"] });
      onClose();
    },
    onError: (e) => message.error(extractErrMsg(e)),
  });

  const pct = pctVsPrev(price, prevClose);
  const submit = () => {
    if (!price || price <= 0) {
      message.warning("정정 단가를 입력하세요");
      return;
    }
    if (price === row.fc_orr_uit_pr) {
      message.warning("기존 단가와 동일합니다");
      return;
    }
    Modal.confirm({
      title: "주문 정정",
      content: `${row.tck_iem_cd} 잔여 ${row.ny_cns_orr_qty}주 단가 $${row.fc_orr_uit_pr.toFixed(2)} → $${price.toFixed(2)} (${fmtPct(pct)})`,
      okText: "정정",
      onOk: () => mut.mutateAsync({ org_order_no: String(row.orr_no), iem_cd: row.tck_iem_cd, price }),
    });
  };

  return (
    <Modal
      title={`주문 정정 — ${row.iem_nm?.trim() || row.tck_iem_cd} (${row.tck_iem_cd}) 잔여 ${row.ny_cns_orr_qty}주`}
      open
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>닫기</Button>
          <Button type="primary" loading={mut.isPending} onClick={submit}>
            정정
          </Button>
        </Space>
      }
      width={480}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          현재 주문가 ${row.fc_orr_uit_pr.toFixed(2)}
          {trdprc > 0 && (
            <> · 현재가 ${trdprc.toFixed(2)}{prevClose ? <> (전일종가 대비 <PctText v={pctVsPrev(trdprc, prevClose)} />)</> : null}</>
          )}
        </Typography.Text>
        <Space size={4} wrap>
          <Typography.Text type="secondary">새 단가 $</Typography.Text>
          <InputNumber size="small" min={0} step={0.5} value={price} onChange={(v) => setPrice(v as number)} style={{ width: 90 }} />
          {trdprc > 0 && (
            <Button size="small" type="link" onClick={() => setPrice(Math.round(trdprc * 100) / 100)}>
              현재가
            </Button>
          )}
        </Space>
        {!Number.isNaN(pct ?? NaN) && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            새 단가 전일종가 대비 <PctText v={pct} />
          </Typography.Text>
        )}
        {prevClose && trdprc > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {(side === "buy" ? LADDER_STEPS : LADDER_STEPS_SELL).map((step) => {
              const amt = Math.round(trdprc * (1 + step / 100) * 100) / 100;
              const selected = !!price && Math.abs(price - amt) < 0.005;
              return (
                <Button
                  key={step}
                  size="small"
                  type={selected ? "primary" : "default"}
                  style={{ lineHeight: 1.2, padding: "2px 8px", height: "auto" }}
                  onClick={() => setPrice(amt)}
                >
                  <span style={{ display: "block", fontSize: 11 }}>${amt.toFixed(2)}</span>
                </Button>
              );
            })}
          </div>
        )}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          정정은 잔여 전체 수량 대상 · 수량 변경은 미지원 (취소 후 재주문)
        </Typography.Text>
      </Space>
    </Modal>
  );
};

// ═══════════════ 매매 탭 — 보유 → 주문 → 주문확인 → 내역 ═══════════════

const TradeCard = () => {
  const { message } = AntApp.useApp();
  const qc = useQueryClient();
  const [orderModal, setOrderModal] = useState<OrderPreset | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modifyRow, setModifyRow] = useState<NhOrderRow | null>(null);
  const [histDays, setHistDays] = useState(10);
  const [tickerFilter, setTickerFilter] = useState<string | undefined>();
  const [detail, setDetail] = useState<{ ticker: string; name: string } | null>(null);

  const balQ = useQuery({
    queryKey: ["nh", "overseas-balance"],
    queryFn: () => nhApi.getOverseasBalance("20601825101"),
    refetchInterval: 60 * 1000,
  });

  const histQ = useQuery({
    queryKey: ["nh", "orders-history", histDays, "0"],
    queryFn: () => nhApi.getOrdersHistory(histDays, "0"),
    refetchInterval: 60 * 1000,
  });

  const positions = balQ.data?.positions ?? [];
  const tickerList = useMemo(
    () => positions.map((p: any) => String(p.iem_cd || "").trim()).filter(Boolean),
    [positions],
  );
  const quotesKey = tickerList.join(",");
  const quotesQ = useQuery({
    queryKey: ["nh", "quotes", quotesKey],
    queryFn: () => nhApi.getQuotes(tickerList),
    enabled: tickerList.length > 0,
    refetchInterval: 60 * 1000,
    staleTime: 30_000,
  });
  const quotesMap = quotesQ.data?.quotes ?? {};

  const syncMut = useMutation({
    mutationFn: () => nhApi.syncFills(7),
    onSuccess: (res) => {
      const m = res.manual;
      message.success(
        m.inserted > 0
          ? `수동 체결 ${m.inserted}건 등록 (조회 ${m.checked}건)`
          : `새로 등록된 체결 없음 (조회 ${m.checked}건)`,
      );
      qc.invalidateQueries({ queryKey: ["nh"] });
      qc.invalidateQueries({ queryKey: ["stocks"] });
    },
    onError: (e) => message.error(extractErrMsg(e)),
  });

  const cancelMut = useMutation({
    mutationFn: nhApi.cancelOrder,
    onSuccess: (res) => {
      message.success(`취소 접수: ${res.rsp_msg ?? "완료"}`);
      qc.invalidateQueries({ queryKey: ["nh"] });
    },
    onError: (e) => message.error(extractErrMsg(e)),
  });

  const openOrder = (preset: OrderPreset) => {
    setOrderModal(preset);
    setModalOpen(true);
  };

  const bal = balQ.data;
  const summary = histQ.data?.summary;
  const screens = Grid.useBreakpoint();
  const showTicker = !!screens.sm;
  const todayYmd = dayjs().format("YYYYMMDD");

  const positionColumns: TableProps<any>["columns"] = [
    {
      title: "종목",
      key: "name",
      render: (_: unknown, r: any) => (
        <Space size={4}>
          <a style={{ whiteSpace: "nowrap" }}
             onClick={() => setDetail({ ticker: r.iem_cd, name: r.iem_nm?.trim() || r.iem_cd })}>
            {showTicker ? (r.iem_nm?.trim() || r.iem_cd) : r.iem_cd}
          </a>
          {showTicker && (
            <a onClick={() => setDetail({ ticker: r.iem_cd, name: r.iem_nm?.trim() || r.iem_cd })}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.iem_cd}</Typography.Text>
            </a>
          )}
        </Space>
      ),
    },
    { title: "수량", dataIndex: "cns_bse_bnc_qty", align: "right" as const, width: 60 },
    {
      title: "평단",
      dataIndex: "fc_avg_phs_pr",
      align: "right" as const,
      responsive: ["md"] as const,
      width: 80,
      render: (v: number) => fmtUsd(v),
    },
    {
      title: "현재가",
      dataIndex: "fc_sec_end_pr",
      align: "right" as const,
      width: 90,
      render: (v: number) => fmtUsd(v),
    },
    {
      title: "당일",
      key: "day",
      align: "right" as const,
      width: 70,
      render: (_: unknown, r: any) => {
        const q = quotesMap[String(r.iem_cd || "").trim()];
        return q ? <PctText v={q.pctchng} /> : "-";
      },
    },
    {
      title: "평가($)",
      dataIndex: "fc_eal_amt",
      align: "right" as const,
      responsive: ["md"] as const,
      width: 90,
      render: (v: number) => fmtUsd(v, 0),
    },
    {
      title: "평가손익",
      dataIndex: "eal_pft_rt",
      align: "right" as const,
      responsive: ["lg"] as const,
      render: (v: number | string, r: any) => {
        const n = Number(v ?? 0);
        return (
          <span style={{ color: n >= 0 ? UP : DOWN }}>
            {n >= 0 ? "+" : ""}{n.toFixed(1)}%
            <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
              ({formatWon(Number(r.krw_eal_amt ?? 0))})
            </Typography.Text>
          </span>
        );
      },
    },
    {
      title: "주문",
      key: "action",
      width: 100,
      align: "right" as const,
      render: (_: unknown, r: any) => (
        <Space size={0}>
          <Button size="small" type="link" danger
                  onClick={() => openOrder({ ticker: r.iem_cd, name: r.iem_nm?.trim(), side: "buy" })}>
            매수
          </Button>
          <Button size="small" type="link"
                  onClick={() => openOrder({ ticker: r.iem_cd, name: r.iem_nm?.trim(), side: "sell" })}>
            매도
          </Button>
        </Space>
      ),
    },
  ];

  const allOrders = useMemo(() => histQ.data?.orders ?? [], [histQ.data]);
  const pendingOrders = useMemo(
    () => allOrders.filter((r) => r.cor_can_dit_nm === "정상" && r.ny_cns_orr_qty > 0),
    [allOrders],
  );
  const filteredOrders = useMemo(
    () => allOrders.filter((r) => !tickerFilter || r.tck_iem_cd === tickerFilter),
    [allOrders, tickerFilter],
  );
  const historyTickers = useMemo(
    () => [...new Set(allOrders.map((r) => r.tck_iem_cd).filter(Boolean))].sort(),
    [allOrders],
  );

  const orderInfoColumns: TableProps<NhOrderRow>["columns"] = [
    {
      title: "구분",
      key: "side",
      width: 60,
      render: (_: unknown, r: NhOrderRow) => (
        <Tag color={r.oss_sby_dit_cd === "2" ? "red" : "blue"} style={{ marginRight: 0 }}>{r.sby_dit_nm}</Tag>
      ),
    },
    {
      title: "종목",
      key: "ticker",
      render: (_: unknown, r: NhOrderRow) => (
        <Space size={4}>
          <span style={{ whiteSpace: "nowrap" }}>
            {showTicker ? (r.iem_nm?.trim() || r.tck_iem_cd) : r.tck_iem_cd}
          </span>
          {showTicker && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.tck_iem_cd}</Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: "유형",
      dataIndex: "ahi_nmn_pr_tp_cd_nm",
      responsive: ["md"] as const,
      width: 100,
    },
    {
      title: "단가",
      align: "right" as const,
      key: "price",
      render: (_: unknown, r: NhOrderRow) => (r.fc_orr_uit_pr ? fmtUsd(r.fc_orr_uit_pr) : "-"),
    },
  ];

  const pendingColumns: TableProps<NhOrderRow>["columns"] = [
    ...orderInfoColumns,
    {
      title: "잔여",
      dataIndex: "ny_cns_orr_qty",
      align: "right" as const,
      width: 60,
      render: (v: number, r: NhOrderRow) => (
        <Space size={4}>
          <span>{v}주</span>
          {r.orr_dt < todayYmd && (
            <Tooltip title="전일 주문 — 장 만료로 자동취소되었을 수 있습니다 (NH 상태 반영 전)">
              <Tag color="warning" style={{ marginRight: 0 }}>전일</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "",
      key: "action",
      width: 90,
      render: (_: unknown, r: NhOrderRow) => (
        <Space size={0}>
          <Button size="small" type="link" onClick={() => setModifyRow(r)}>
            정정
          </Button>
          <Button
            size="small"
            type="link"
            danger
            loading={cancelMut.isPending && cancelMut.variables?.org_order_no === String(r.orr_no)}
            onClick={() => {
              Modal.confirm({
                title: "주문 취소",
                content: `${r.tck_iem_cd} ${r.ny_cns_orr_qty}주 미체결 주문을 취소할까요?`,
                okText: "취소",
                okButtonProps: { danger: true },
                onOk: () =>
                  cancelMut.mutateAsync({ org_order_no: String(r.orr_no), iem_cd: r.tck_iem_cd }),
              });
            }}
          >
            취소
          </Button>
        </Space>
      ),
    },
  ];

  const historyColumns: TableProps<NhOrderRow>["columns"] = [
    {
      title: "일자",
      dataIndex: "orr_dt",
      width: 70,
      render: (v: string) => dayjs(v, "YYYYMMDD").format("MM/DD"),
    },
    ...orderInfoColumns,
    {
      title: "전일비",
      key: "pct_vs_prev",
      align: "right" as const,
      width: 80,
      responsive: ["md"] as const,
      render: (_: unknown, r: NhOrderRow) =>
        r.pct_vs_prev !== null && r.pct_vs_prev !== undefined ? <PctText v={r.pct_vs_prev} /> : "-",
    },
    {
      title: "주문수량",
      dataIndex: "orr_qty",
      align: "right" as const,
      width: 80,
      render: (v: number) => `${v}주`,
    },
    {
      title: "체결수량",
      dataIndex: "cns_qty",
      align: "right" as const,
      width: 80,
      responsive: ["sm"] as const,
      render: (v: number) => (v > 0 ? `${v}주` : "-"),
    },
    {
      title: "체결단가",
      key: "cns_pr",
      align: "right" as const,
      width: 90,
      responsive: ["md"] as const,
      render: (_: unknown, r: NhOrderRow) => (r.cns_qty > 0 && r.cns_pr ? fmtUsd(r.cns_pr) : "-"),
    },
    {
      title: "상태",
      key: "status",
      render: (_: unknown, r: NhOrderRow) => {
        if (r.cor_can_dit_nm !== "정상") return <Tag>{r.cor_can_dit_nm}</Tag>;
        if (r.ny_cns_orr_qty > 0) return <Tag color="processing">미체결 {r.ny_cns_orr_qty}주</Tag>;
        if (r.cns_qty > 0) return <Tag color="success">체결</Tag>;
        return <Tag>{r.orr_sts_nm || "확인"}</Tag>;
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card
        size="small"
        title={`NH 해외 보유 (${bal?.act_no ?? "-"})`}
        extra={
          <Space size={8}>
            <Tooltip title="NH 체결내역 → 거래내역 등록 (수동 주문 포함)">
              <Button size="small" icon={<SyncOutlined />} loading={syncMut.isPending} onClick={() => syncMut.mutate()}>
                체결 동기화
              </Button>
            </Tooltip>
            <Button size="small" type="primary" icon={<PlusOutlined />}
                    onClick={() => openOrder({ side: "buy" })}>
              신규 종목 주문
            </Button>
          </Space>
        }
      >
        {balQ.isLoading || histQ.isLoading ? (
          <Spin />
        ) : balQ.isError ? (
          <QueryError error={balQ.error} onRetry={() => balQ.refetch()} />
        ) : histQ.isError ? (
          <QueryError error={histQ.error} onRetry={() => histQ.refetch()} />
        ) : (
          <>
            <Space wrap size="large" style={{ marginBottom: 8 }}>
              <Statistic title="달러 예수금" value={fmtUsd(bal?.summary?.fc_dca, 0)} valueStyle={{ fontSize: 16 }} />
              <Statistic
                title="실효 가용 (예수금−미체결매수)"
                value={fmtUsd((bal?.summary?.fc_dca ?? 0) - (summary?.pending_buy_usd ?? 0), 0)}
                valueStyle={{ fontSize: 16, color: (bal?.summary?.fc_dca ?? 0) - (summary?.pending_buy_usd ?? 0) > 0 ? undefined : DOWN }}
              />
              <Statistic title="평가금액" value={fmtUsd(bal?.summary?.fc_eal_amt, 0)} valueStyle={{ fontSize: 16 }} />
              <Statistic
                title="미체결 매수 합"
                value={fmtUsd(summary?.pending_buy_usd, 0)}
                valueStyle={{ fontSize: 16, color: UP }}
              />
              <Statistic
                title="미체결 매도 합"
                value={fmtUsd(summary?.pending_sell_usd, 0)}
                valueStyle={{ fontSize: 16, color: DOWN }}
              />
              <Statistic title="원화 환산" value={formatWon(bal?.summary?.eal_amt_sum ?? 0)} valueStyle={{ fontSize: 16 }} />
            </Space>
            <Table
              size="small"
              rowKey="iem_cd"
              columns={positionColumns}
              dataSource={positions}
              pagination={false}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: "보유 종목 없음" }}
            />
          </>
        )}
      </Card>

      <Card size="small" title={`미체결 주문 (${pendingOrders.length}건)`}>
        <Table
          size="small"
          rowKey="orr_no"
          columns={pendingColumns}
          dataSource={pendingOrders}
          pagination={false}
          loading={histQ.isLoading}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "미체결 주문이 없습니다" }}
        />
      </Card>

      <Card
        size="small"
        title="주문 내역"
        extra={
          <Space size={8}>
            <Select
              size="small"
              allowClear
              placeholder="종목"
              value={tickerFilter}
              onChange={(v) => setTickerFilter(v)}
              style={{ width: 110 }}
              options={historyTickers.map((t) => ({ value: t, label: t }))}
            />
            <Select
              size="small"
              value={histDays}
              onChange={setHistDays}
              style={{ width: 80 }}
              options={[5, 10, 20, 31].map((d) => ({ value: d, label: `${d}일` }))}
            />
          </Space>
        }
      >
        <Table
          size="small"
          rowKey="orr_no"
          columns={historyColumns}
          dataSource={filteredOrders}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          loading={histQ.isLoading}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "주문 내역이 없습니다" }}
        />
      </Card>

      <OrderModal
        open={modalOpen}
        preset={orderModal}
        key={`${orderModal?.ticker ?? "search"}-${orderModal?.side ?? "buy"}-${modalOpen}`}
        onClose={() => {
          setModalOpen(false);
          setOrderModal(null);
        }}
      />

      {modifyRow && (
        <ModifyModal
          row={modifyRow}
          prevClose={modifyRow.prev_close ?? quotesMap[modifyRow.tck_iem_cd]?.prev_close ?? null}
          trdprc={quotesMap[modifyRow.tck_iem_cd]?.trdprc ?? 0}
          onClose={() => setModifyRow(null)}
        />
      )}

      <StockDetailDrawer
        open={!!detail}
        onClose={() => setDetail(null)}
        stockId={detail?.ticker ?? ""}
        stockName={detail?.name ?? ""}
      />
    </div>
  );
};

// ═══════════════ 자동매매 ═══════════════

/** 설정 카드 */
const ConfigCard = () => {
  const { message } = AntApp.useApp();
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ["nh", "auto-config"], queryFn: nhApi.getAutoConfig });
  const [actNo, setActNo] = useState<string | undefined>();
  const [amount, setAmount] = useState<number | undefined>();
  const [rsi, setRsi] = useState<number | undefined>();

  const saveMut = useMutation({
    mutationFn: nhApi.updateAutoConfig,
    onSuccess: () => {
      message.success("저장되었습니다");
      qc.invalidateQueries({ queryKey: ["nh"] });
    },
    onError: (e) => message.error(extractErrMsg(e)),
  });

  const changeMode = (mode: string) => {
    if (mode === "live") {
      Modal.confirm({
        title: "실주문 모드 전환",
        content: "이제 신호 발생 시 실제 주문이 접수됩니다. 전환할까요?",
        okText: "LIVE 전환",
        okButtonProps: { danger: true },
        onOk: () => saveMut.mutate({ mode: "live" }),
      });
    } else {
      saveMut.mutate({ mode: mode as "off" | "dry_run" });
    }
  };

  if (cfgQ.isLoading) return <Spin />;
  if (cfgQ.isError) return <QueryError error={cfgQ.error} onRetry={() => cfgQ.refetch()} />;
  if (!cfgQ.data) return null;
  const cfg = cfgQ.data;

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>자동매매 설정</span>
          <Tag color={MODE_META[cfg.mode]?.color}>{MODE_META[cfg.mode]?.label}</Tag>
        </Space>
      }
      extra={
        <Tooltip title="지금 1회 판정·실행 (스케줄러와 동일 로직)">
          <Button size="small" icon={<PlayCircleOutlined />} loading={saveMut.isPending}
                  onClick={() => {
                    Modal.confirm({
                      title: "수동 실행",
                      content: "현재 모드 기준으로 1회 판정·실행합니다. 실행할까요?",
                      onOk: async () => {
                        try {
                          await nhApi.runOnce();
                          message.success("실행 완료 — 신호 기록 확인");
                          qc.invalidateQueries({ queryKey: ["nh"] });
                        } catch (e) {
                          message.error(extractErrMsg(e));
                        }
                      },
                    });
                  }}>
            수동 실행
          </Button>
        </Tooltip>
      }
    >
      <Space wrap size="middle" align="center">
        <Space size={4}>
          <Typography.Text type="secondary">모드</Typography.Text>
          <Segmented
            value={cfg.mode}
            onChange={(v) => changeMode(v as string)}
            options={[
              { label: "정지", value: "off" },
              { label: "Dry-run", value: "dry_run" },
              { label: "LIVE", value: "live" },
            ]}
          />
        </Space>
        <Space size={4}>
          <Typography.Text type="secondary">1회 매수 $</Typography.Text>
          <InputNumber
            size="small"
            min={100}
            step={100}
            value={amount ?? cfg.per_buy_amount}
            onChange={(v) => setAmount(v as number)}
            style={{ width: 90 }}
          />
        </Space>
        <Space size={4}>
          <Typography.Text type="secondary">매도 RSI ≥</Typography.Text>
          <InputNumber
            size="small"
            min={50}
            max={95}
            value={rsi ?? cfg.sell_rsi_threshold}
            onChange={(v) => setRsi(v as number)}
            style={{ width: 70 }}
          />
        </Space>
        <Space size={4}>
          <Typography.Text type="secondary">계좌</Typography.Text>
          <Select
            size="small"
            value={actNo ?? cfg.act_no}
            onChange={setActNo}
            style={{ width: 150 }}
            options={[
              { value: "20601825101", label: "20601825101 (위탁)" },
              { value: "20601687967", label: "20601687967 (위탁)" },
            ]}
          />
        </Space>
        <Button
          size="small"
          type="primary"
          loading={saveMut.isPending}
          onClick={() => {
            const body: Record<string, unknown> = {};
            if (amount !== undefined && amount !== cfg.per_buy_amount) body.per_buy_amount = amount;
            if (rsi !== undefined && rsi !== cfg.sell_rsi_threshold) body.sell_rsi_threshold = rsi;
            if (actNo !== undefined && actNo !== cfg.act_no) body.act_no = actNo;
            if (Object.keys(body).length === 0) {
              message.info("변경된 값이 없습니다");
              return;
            }
            saveMut.mutate(body);
          }}
        >
          저장
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          30분 주기 판정 · 미국 장중(KST 22:30~06:30)에만 실행
        </Typography.Text>
      </Space>
    </Card>
  );
};

/** 판정 현황 테이블 */
const JudgmentTable = () => {
  const q = useQuery({
    queryKey: ["nh", "judgment"],
    queryFn: nhApi.getJudgment,
    refetchInterval: 5 * 60 * 1000,
  });
  if (q.isLoading) return <Spin />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return null;
  const j = q.data;

  const columns: TableProps<NhJudgmentCategory>["columns"] = [
    {
      title: "카테고리",
      dataIndex: "label",
      render: (_: string, r: NhJudgmentCategory) => (
        <Space size={4}>
          <span>{r.label}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{r.us_ticker}</Typography.Text>
        </Space>
      ),
    },
    {
      title: "현재/목표",
      key: "pct",
      render: (_: unknown, r: NhJudgmentCategory) => (
        <span>
          {Number(r.current_pct ?? 0).toFixed(1)}% / {r.target_pct != null ? Number(r.target_pct).toFixed(1) : "-"}%
        </span>
      ),
    },
    {
      title: "괴리",
      dataIndex: "dev_pct",
      align: "right" as const,
      render: (v: number | string | null) => {
        if (v === null || v === undefined) return "-";
        const n = Number(v);
        return (
          <span style={{ color: n < 0 ? UP : n > 0 ? DOWN : undefined }}>
            {fmtPct(n)}
          </span>
        );
      },
    },
    {
      title: "52주dd",
      dataIndex: "dd_52w",
      align: "right" as const,
      render: (v: number | string | null) =>
        v === null || v === undefined ? "-" : `${Number(v).toFixed(1)}%`,
    },
    {
      title: "RSI",
      dataIndex: "rsi14",
      align: "right" as const,
      render: (v: number | string | null) => {
        if (v === null || v === undefined) return "-";
        const n = Number(v);
        return <span style={{ color: n >= 70 ? DOWN : undefined }}>{n.toFixed(0)}</span>;
      },
    },
    {
      title: "신호",
      dataIndex: "signal",
      width: 90,
      render: (s: string) => <Tag color={SIGNAL_META[s]?.color}>{SIGNAL_META[s]?.label}</Tag>,
    },
  ];

  return (
    <Card
      size="small"
      title="판정 현황"
      extra={
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          밴드 ±{j.band_pct}%p · 게이트 {j.gate.entry_dd}%/{j.gate.accel_dd}% · FX {j.fx_usd?.toFixed(0)}
        </Typography.Text>
      }
    >
      <Table
        size="small"
        rowKey="category_key"
        columns={columns}
        dataSource={j.categories}
        pagination={false}
      />
    </Card>
  );
};

/** 신호·주문 기록 */
const SignalsTable = () => {
  const q = useQuery({
    queryKey: ["nh", "signals"],
    queryFn: () => nhApi.getSignals(50),
    refetchInterval: 5 * 60 * 1000,
  });
  if (q.isLoading) return <Spin />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => q.refetch()} />;
  const signals = q.data?.signals ?? [];

  const columns: TableProps<NhSignal>["columns"] = [
    {
      title: "일자",
      dataIndex: "signal_date",
      width: 90,
      render: (v: string) => dayjs(v).format("MM/DD"),
    },
    {
      title: "종목",
      key: "ticker",
      render: (_: unknown, r: NhSignal) => (
        <Space size={4}>
          <Tag color={r.side === "buy" ? "red" : "blue"}>{r.side === "buy" ? "매수" : "매도"}</Tag>
          <span>{r.ticker}</span>
        </Space>
      ),
    },
    {
      title: "카테고리",
      dataIndex: "category_label",
      responsive: ["md"] as const,
    },
    {
      title: "금액",
      dataIndex: "amount_usd",
      align: "right" as const,
      render: (v: number | null) => fmtUsd(v, 0),
    },
    {
      title: "수량",
      dataIndex: "qty",
      align: "right" as const,
      responsive: ["sm"] as const,
      render: (v: number | null) => (v ? `${v}주` : "-"),
    },
    {
      title: "상태",
      dataIndex: "status",
      width: 90,
      render: (s: string, r: NhSignal) => (
        <Tooltip title={r.rsp_msg || undefined}>
          <Tag color={STATUS_META[s]?.color ?? "default"}>{STATUS_META[s]?.label ?? s}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "판정",
      key: "snapshot",
      responsive: ["lg"] as const,
      render: (_: unknown, r: NhSignal) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          괴리 {fmtPct(r.dev_pct)} · dd {r.dd_52w != null ? Number(r.dd_52w).toFixed(1) : "-"}% · RSI{" "}
          {r.rsi14 != null ? Number(r.rsi14).toFixed(0) : "-"}
        </Typography.Text>
      ),
    },
  ];

  return (
    <Card size="small" title="신호·주문 기록">
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={signals}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: "아직 신호가 없습니다" }}
      />
    </Card>
  );
};

// ═══════════════ 페이지 ═══════════════

const TAB_ITEMS = [
  { value: "trade", label: "매매" },
  { value: "auto", label: "자동매매" },
];

export default function NhTradingPage() {
  const [tab, setTab] = useTabSearchParam("view", "trade", {
    manual: "trade",
    orders: "trade",
    holdings: "trade",
  });

  return (
    <div className="app-main" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Segmented value={tab} onChange={(v) => setTab(v as string)} options={TAB_ITEMS} />
      {tab === "trade" && <TradeCard />}
      {tab === "auto" && (
        <>
          <ConfigCard />
          <JudgmentTable />
          <SignalsTable />
        </>
      )}
    </div>
  );
}
